class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    let submitTimeout;
    this.debouncedOnSubmit = (event) => {
      clearTimeout(submitTimeout);
      submitTimeout = setTimeout(() => {
        this.onSubmitHandler(event);
      }, 300);
    };
    this.debouncedOnSubmit.cancel = () => {
      clearTimeout(submitTimeout);
    };

    const facetForm = this.querySelector('form');
    facetForm.addEventListener('input', this.debouncedOnSubmit.bind(this));

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);
  }

  connectedCallback() {
    FacetFiltersForm.syncFilterCheckboxStates();
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    document.querySelectorAll('facet-filters-form').forEach((facetForm) => {
      if (facetForm.debouncedOnSubmit && typeof facetForm.debouncedOnSubmit.cancel === 'function') {
        facetForm.debouncedOnSubmit.cancel();
      }
    });

    FacetFiltersForm.latestRequestId = (FacetFiltersForm.latestRequestId || 0) + 1;
    const requestId = FacetFiltersForm.latestRequestId;

    FacetFiltersForm.currentSearchParams = searchParams;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();
    const countContainer = document.getElementById('ProductCount');
    const countContainerDesktop = document.getElementById('ProductCountDesktop');
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.remove('hidden'));
    document.getElementById('ProductGridContainer').querySelector('.collection').classList.add('loading');
    if (countContainer) {
      countContainer.classList.add('loading');
    }
    if (countContainerDesktop) {
      countContainerDesktop.classList.add('loading');
    }

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event, requestId)
        : FacetFiltersForm.renderSectionFromFetch(url, event, requestId);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event, requestId) {
    if (FacetFiltersForm.abortController) {
      FacetFiltersForm.abortController.abort();
    }
    FacetFiltersForm.abortController = new AbortController();
    const signal = FacetFiltersForm.abortController.signal;

    fetch(url, { signal })
      .then((response) => response.text())
      .then((responseText) => {
        if (requestId !== FacetFiltersForm.latestRequestId) return;
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error(error);
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event, requestId) {
    if (requestId !== FacetFiltersForm.latestRequestId) return;
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(html.innerHTML);
  }

  static renderProductGridContainer(html) {
    const container = document.getElementById('ProductGridContainer');
    if (!container) return;
    const parsedContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer');
    if (parsedContainer) {
      container.innerHTML = parsedContainer.innerHTML;
    }

    container.querySelectorAll('.scroll-trigger').forEach((element) => {
      element.classList.add('scroll-trigger--cancel');
    });
  }

  static renderProductCount(html) {
    const parsedDoc = new DOMParser().parseFromString(html, 'text/html');
    const countElement = parsedDoc.getElementById('ProductCount');
    if (!countElement) return;
    const count = countElement.innerHTML;
    const container = document.getElementById('ProductCount');
    const containerDesktop = document.getElementById('ProductCountDesktop');
    if (container) {
      container.innerHTML = count;
      container.classList.remove('loading');
    }
    if (containerDesktop) {
      containerDesktop.innerHTML = count;
      containerDesktop.classList.remove('loading');
    }
    const loadingSpinners = document.querySelectorAll(
      '.facets-container .loading__spinner, facet-filters-form .loading__spinner'
    );
    loadingSpinners.forEach((spinner) => spinner.classList.add('hidden'));
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );
    const facetDetailsElementsFromDom = document.querySelectorAll(
      '#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter, #FacetFiltersPillsForm .js-filter'
    );

    const facetsWrapperFromFetch = parsedHTML.getElementById('FacetsWrapperDesktop');
    const facetsWrapperInDom = document.getElementById('FacetsWrapperDesktop');
    const mobileWrapperFromFetch = parsedHTML.getElementById('FacetsWrapperMobile');
    const mobileWrapperInDom = document.getElementById('FacetsWrapperMobile');
    const syncedDesktopWrapper = !!(facetsWrapperFromFetch && facetsWrapperInDom);
    const syncedMobileWrapper = !!(mobileWrapperFromFetch && mobileWrapperInDom);

    if (syncedDesktopWrapper) {
      facetsWrapperInDom.innerHTML = facetsWrapperFromFetch.innerHTML;
    }
    if (syncedMobileWrapper) {
      mobileWrapperInDom.innerHTML = mobileWrapperFromFetch.innerHTML;
    }

    const matchesId = (element) => {
      const jsFilter = event ? event.target.closest('.js-filter') : undefined;
      return jsFilter ? element.id === jsFilter.id : false;
    };

    const facetsToRender = Array.from(facetDetailsElementsFromFetch).filter((element) => !matchesId(element));
    const countsToRender = Array.from(facetDetailsElementsFromFetch).find(matchesId);

    if (!syncedDesktopWrapper && !syncedMobileWrapper) {
      Array.from(facetDetailsElementsFromDom).forEach((currentElement) => {
        if (!Array.from(facetDetailsElementsFromFetch).some(({ id }) => currentElement.id === id)) {
          currentElement.remove();
        }
      });

      facetsToRender.forEach((elementToRender, index) => {
        const currentElement = document.getElementById(elementToRender.id);
        if (currentElement) {
          document.getElementById(elementToRender.id).innerHTML = elementToRender.innerHTML;
        } else {
          if (index > 0) {
            const { className: previousElementClassName, id: previousElementId } = facetsToRender[index - 1];
            if (elementToRender.className === previousElementClassName) {
              document.getElementById(previousElementId).after(elementToRender);
              return;
            }
          }

          if (elementToRender.parentElement) {
            const targetParent = document.querySelector(`#${elementToRender.parentElement.id} .js-filter`);
            if (targetParent) {
              targetParent.before(elementToRender);
            }
          }
        }
      });
    }

    FacetFiltersForm.renderActiveFacets(parsedHTML);
    FacetFiltersForm.renderAdditionalElements(parsedHTML);

    if (countsToRender && event) {
      const clickedForm = event.target.closest('form');
      const skipCounts =
        (syncedDesktopWrapper && clickedForm?.id === 'FacetFiltersForm') ||
        (syncedMobileWrapper && clickedForm?.id === 'FacetFiltersFormMobile');
      const closestJSFilterID = event.target.closest('.js-filter')?.id;

      if (closestJSFilterID && !skipCounts) {
        FacetFiltersForm.renderCounts(countsToRender, event.target.closest('.js-filter'));
        FacetFiltersForm.renderMobileCounts(countsToRender, document.getElementById(closestJSFilterID));

        const newFacetDetailsElement = document.getElementById(closestJSFilterID);
        const newElementSelector = newFacetDetailsElement.classList.contains('mobile-facets__details')
          ? `.mobile-facets__close-button`
          : `.facets__summary, .ec-facets__filter-summary`;
        const newElementToActivate = newFacetDetailsElement.querySelector(newElementSelector);

        const isTextInput = event.target.getAttribute('type') === 'text';

        if (newElementToActivate && !isTextInput) newElementToActivate.focus();
      }
    }

    FacetFiltersForm.syncFilterCheckboxStates();
  }

  static syncFilterCheckboxStates() {
    const searchParams = typeof FacetFiltersForm.currentSearchParams === 'string'
      ? FacetFiltersForm.currentSearchParams
      : window.location.search.slice(1);
    const params = new URLSearchParams(searchParams);

    const standardInputs = document.querySelectorAll('.ec-facets__checkbox-input');
    standardInputs.forEach((input) => {
      if (!input.name) return;
      const values = params.getAll(input.name);
      const shouldBeChecked = values.includes(input.value);
      input.checked = shouldBeChecked;
      
      const label = input.closest('.ec-facets__checkbox-label');
      if (label) {
        if (shouldBeChecked) {
          label.classList.add('ec-facets__checkbox-label--checked');
        } else {
          label.classList.remove('ec-facets__checkbox-label--checked');
        }
      }
    });

    const minPriceRaw = params.get('filter.v.price.gte');
    const maxPriceRaw = params.get('filter.v.price.lte');
    const minPrice = minPriceRaw ? parseFloat(minPriceRaw) : null;
    const maxPrice = maxPriceRaw ? parseFloat(maxPriceRaw) : null;

    const priceInputs = document.querySelectorAll('.ec-price-facet__bucket-input');
    const tolerance = 1.0;
    const hasAnyPriceFilter = minPrice !== null || maxPrice !== null;

    priceInputs.forEach((input) => {
      const bucketMinRaw = input.dataset.min;
      const bucketMaxRaw = input.dataset.max;
      const bucketMin = bucketMinRaw ? parseFloat(bucketMinRaw) : null;
      const bucketMax = bucketMaxRaw ? parseFloat(bucketMaxRaw) : null;

      const minWithinRange = minPrice === null
        || (bucketMin !== null && bucketMin >= minPrice - tolerance);
      const maxWithinRange = maxPrice === null
        || (bucketMax !== null && bucketMax <= maxPrice + tolerance);

      const shouldBeChecked = hasAnyPriceFilter && minWithinRange && maxWithinRange;

      input.checked = shouldBeChecked;
      const label = input.closest('.ec-facets__checkbox-label');
      if (label) {
        if (shouldBeChecked) {
          label.classList.add('ec-facets__checkbox-label--checked');
        } else {
          label.classList.remove('ec-facets__checkbox-label--checked');
        }
      }
    });

    if (!minPriceRaw && !maxPriceRaw) {
      document.querySelectorAll('[data-price-bucket-min], [data-price-bucket-max], [data-type="min-hidden"], [data-type="max-hidden"]').forEach((input) => {
        input.disabled = true;
        input.value = '';
        input.removeAttribute('value');
      });
      document.querySelectorAll('.ec-price-facet__input').forEach((input) => {
        input.value = '';
      });
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelectors = ['.active-facets-mobile', '.active-facets-desktop'];

    activeFacetElementSelectors.forEach((selector) => {
      const activeFacetsElement = html.querySelector(selector);
      const targetElement = document.querySelector(selector);
      if (!targetElement) return;
      if (activeFacetsElement) {
        targetElement.outerHTML = activeFacetsElement.outerHTML;
      } else {
        targetElement.classList.add('hidden');
        targetElement.style.display = 'none';
        const list = targetElement.querySelector('.ec-facets-active__list');
        if (list) list.replaceChildren();
      }
    });

    FacetFiltersForm.toggleActiveFacets(false);
  }

  static renderAdditionalElements(html) {
    const mobileElementSelectors = ['.mobile-facets__open', '.mobile-facets__count', '.sorting', '.ec-collection__mobile-filter-btn'];

    mobileElementSelectors.forEach((selector) => {
      if (!html.querySelector(selector)) return;
      const targetElement = document.querySelector(selector);
      if (targetElement) {
        targetElement.innerHTML = html.querySelector(selector).innerHTML;
      }
    });

    const mobileForm = document.getElementById('FacetFiltersFormMobile');
    if (mobileForm) {
      const menuDrawer = mobileForm.closest('menu-drawer');
      if (menuDrawer) menuDrawer.bindEvents();
    }
  }

  static renderCounts(source, target) {
    const targetSummary = target.querySelector('.facets__summary') || target.querySelector('.ec-facets__filter-summary');
    const sourceSummary = source.querySelector('.facets__summary') || source.querySelector('.ec-facets__filter-summary');

    if (sourceSummary && targetSummary) {
      targetSummary.outerHTML = sourceSummary.outerHTML;
    }

    const targetHeaderElement = target.querySelector('.facets__header');
    const sourceHeaderElement = source.querySelector('.facets__header');

    if (sourceHeaderElement && targetHeaderElement) {
      targetHeaderElement.outerHTML = sourceHeaderElement.outerHTML;
    }

    const targetWrapElement = target.querySelector('.facets-wrap') || target.querySelector('.ec-facets__filter-content');
    const sourceWrapElement = source.querySelector('.facets-wrap') || source.querySelector('.ec-facets__filter-content');

    if (sourceWrapElement && targetWrapElement) {
      const isShowingMore = Boolean(target.querySelector('show-more-button .label-show-more.hidden'));
      if (isShowingMore) {
        sourceWrapElement
          .querySelectorAll('.facets__item.hidden')
          .forEach((hiddenItem) => hiddenItem.classList.replace('hidden', 'show-more-item'));
      }

      targetWrapElement.outerHTML = sourceWrapElement.outerHTML;
    }
  }

  static renderMobileCounts(source, target) {
    const targetFacetsList = target.querySelector('.mobile-facets__list') || target.querySelector('.ec-facets__filter-content');
    const sourceFacetsList = source.querySelector('.mobile-facets__list') || source.querySelector('.ec-facets__filter-content');

    if (sourceFacetsList && targetFacetsList) {
      targetFacetsList.outerHTML = sourceFacetsList.outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  static getSections() {
    const productGrid = document.getElementById('product-grid');
    const sidebar = document.getElementById('ec-collection-filters');
    const gridContainer = document.getElementById('ProductGridContainer');
    const sectionId =
      productGrid?.dataset?.id ||
      sidebar?.dataset?.id ||
      gridContainer?.dataset?.sectionId ||
      '';
    return [{ section: sectionId }];
  }

  static syncPriceBucketFormState(form) {
    if (!form) return;
    form.querySelectorAll('[data-price-facet-buckets]').forEach((card) => {
      const minHiddenInput = card.querySelector('[data-price-bucket-min]');
      const maxHiddenInput = card.querySelector('[data-price-bucket-max]');
      if (!minHiddenInput || !maxHiddenInput) return;

      const allBuckets = Array.from(card.querySelectorAll('.ec-price-facet__bucket-input')).sort(
        (a, b) => parseInt(a.getAttribute('data-price-bucket'), 10) - parseInt(b.getAttribute('data-price-bucket'), 10)
      );
      const checkedBuckets = allBuckets.filter((input) => input.checked);

      if (checkedBuckets.length === 0) {
        minHiddenInput.disabled = true;
        minHiddenInput.value = '';
        minHiddenInput.removeAttribute('value');
        maxHiddenInput.disabled = true;
        maxHiddenInput.value = '';
        maxHiddenInput.removeAttribute('value');
        return;
      }

      const firstBucket = allBuckets[allBuckets.indexOf(checkedBuckets[0])];
      const lastBucket = allBuckets[allBuckets.indexOf(checkedBuckets[checkedBuckets.length - 1])];
      const minPrice = firstBucket.dataset.min || null;
      const maxPrice = lastBucket.dataset.max || null;

      if (minPrice === null) {
        minHiddenInput.disabled = true;
        minHiddenInput.value = '';
        minHiddenInput.removeAttribute('value');
      } else {
        minHiddenInput.disabled = false;
        minHiddenInput.value = minPrice;
      }

      if (maxPrice === null) {
        maxHiddenInput.disabled = true;
        maxHiddenInput.value = '';
        maxHiddenInput.removeAttribute('value');
      } else {
        maxHiddenInput.disabled = false;
        maxHiddenInput.value = maxPrice;
      }
    });
  }

  createSearchParams(form) {
    FacetFiltersForm.syncPriceBucketFormState(form);
    const formData = new FormData(form);
    return new URLSearchParams(formData).toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const sortFilterForms = document.querySelectorAll('facet-filters-form form');
    const formElement = this.querySelector('form') || (event.target && event.target.closest('form')) || document.querySelector('facet-filters-form form');
    
    if (event.srcElement && event.srcElement.className == 'mobile-facets__checkbox') {
      const searchParams = this.createSearchParams(formElement);
      this.onSubmitForm(searchParams, event);
    } else {
      const forms = [];
      const isMobile = formElement && formElement.id === 'FacetFiltersFormMobile';

      sortFilterForms.forEach((form) => {
        if (!isMobile) {
          if (form.id === 'FacetSortForm' || form.id === 'FacetFiltersForm' || form.id === 'FacetSortDrawerForm') {
            forms.push(this.createSearchParams(form));
          }
        } else if (form.id === 'FacetFiltersFormMobile') {
          forms.push(this.createSearchParams(form));
        }
      });
      const finalParams = forms.join('&');
      this.onSubmitForm(finalParams, event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    FacetFiltersForm.toggleActiveFacets();
    const anchor = event.target.closest('a') || event.currentTarget;
    const href = anchor ? anchor.getAttribute('href') || anchor.href : '';
    const url =
      href.indexOf('?') == -1
        ? ''
        : href.slice(href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.currentSearchParams = window.location.search.slice(1);
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
      element.addEventListener('keydown', this.onKeyDown.bind(this));
    });
    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  onKeyDown(event) {
    if (event.metaKey) return;

    const pattern = /[0-9]|\.|,|'| |Tab|Backspace|Enter|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Delete|Escape/;
    if (!event.key.match(pattern)) event.preventDefault();
  }

  setMinAndMaxValues() {
    const inputs = this.querySelectorAll('input');
    const minInput = inputs[0];
    const maxInput = inputs[1];
    if (maxInput.value) minInput.setAttribute('data-max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('data-min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('data-min', 0);
    if (maxInput.value === '') minInput.setAttribute('data-max', maxInput.getAttribute('data-max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('data-min'));
    const max = Number(input.getAttribute('data-max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    facetLink.setAttribute('role', 'button');
    facetLink.addEventListener('click', this.closeFilter.bind(this));
    facetLink.addEventListener('keyup', (event) => {
      event.preventDefault();
      if (event.code.toUpperCase() === 'SPACE') this.closeFilter(event);
    });
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
    form.onActiveFilterClick(event);
  }
}

customElements.define('facet-remove', FacetRemove);
